/**
 * ManageBookingPage — the page a customer reaches from the link in their
 * confirmation email, at /booking/:token.
 *
 * The half of E20 a customer touches. The API landed first, which is the state
 * worth distrusting: without this page the endpoints existed and every
 * cancellation was still a phone call.
 *
 * NO APP CHROME AND NO LOGIN. The customer booked without an account and has no
 * way to make one — the token in the URL is the credential. That is also why
 * this page shows only their own appointment and never the merchant's calendar.
 *
 * THREE STATES, and the third is the one that matters. Loaded, cancelled, and
 * "that link is no longer valid" — which is what an unknown token AND an
 * already-cancelled booking both produce, deliberately, so the page cannot be
 * used to confirm that a given token was ever real.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { t } from '../../i18n';
import ManageCancelConfirm from './ManageCancelConfirm';
import { BOOKING_LOCALE } from './publicBookingTime';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.buzzbitx.com';

interface ManagedBooking {
  id: string;
  scheduledAt: string;
  durationMin: number;
  serviceName: string | null;
  joinUrl: string | null;
  bookingSlug: string | null;
}

interface Slot {
  start: string;
  end: string;
}

function whenLabel(iso: string): string {
  return new Date(iso).toLocaleString(BOOKING_LOCALE, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(BOOKING_LOCALE, { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function ManageBookingPage(): React.ReactElement {
  const { token = '' } = useParams();
  const [booking, setBooking] = useState<ManagedBooking | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [movedTo, setMovedTo] = useState<string | null>(null);
  // Cancelling asks first: the button opens this, and only the dialog's
  // confirm reaches the server. An accidental tap used to cancel outright.
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/booking-page/manage/${token}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message ?? 'That booking link is no longer valid.');
      setBooking(((json?.data ?? json) as { booking: ManagedBooking }).booking);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'That booking link is no longer valid.');
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const cancel = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/booking-page/manage/${token}/cancel`, { method: 'POST' });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message ?? 'We could not cancel that.');
      setCancelled(true);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'We could not cancel that.');
    } finally {
      // Either way the dialog is done: cancelled shows its own page, and a
      // refusal is read on the page beside the booking that is still there.
      setConfirmingCancel(false);
      setBusy(false);
    }
  }, [token]);

  const openPicker = useCallback(async () => {
    setPicking(true);
    setActionError(null);
    if (booking?.bookingSlug == null) return;
    try {
      const res = await fetch(`${API_URL}/api/booking-page/public/${booking.bookingSlug}`);
      const json = await res.json();
      setSlots(((json?.data ?? json) as { slots: Slot[] }).slots ?? []);
    } catch {
      // A failed slot list must not break the cancel button beside it.
      setSlots([]);
    }
  }, [booking]);

  const reschedule = useCallback(async (startsAt: string) => {
    setBusy(true);
    setActionError(null);
    try {
      const res = await fetch(`${API_URL}/api/booking-page/manage/${token}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startsAt }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message ?? 'We could not move that booking.');
      setMovedTo(startsAt);
      setPicking(false);
      await load();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'We could not move that booking.');
    } finally {
      setBusy(false);
    }
  }, [token, load]);

  const grouped = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of slots) {
      const key = new Date(s.start).toDateString();
      map.set(key, [...(map.get(key) ?? []), s]);
    }
    return [...map.entries()].slice(0, 7);
  }, [slots]);

  if (loadError !== null) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center" data-testid="manage-invalid">
        <h1 className="text-lg font-semibold text-gray-900">{t('manage.invalidTitle')}</h1>
        <p className="mt-2 text-sm text-gray-500">{t('manage.invalidBody')}</p>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center" data-testid="manage-cancelled">
        <h1 className="text-lg font-semibold text-gray-900">{t('manage.cancelledTitle')}</h1>
        <p className="mt-2 text-sm text-gray-500">{t('manage.cancelledBody')}</p>
      </div>
    );
  }

  if (booking === null) {
    return <div className="mx-auto max-w-md px-4 py-16 text-center text-sm text-gray-400">{t('manage.loading')}</div>;
  }

  return (
    <div className="mx-auto max-w-md px-4 py-12 pb-cookie-banner [--pb-cookie-base:3rem]" data-testid="manage-booking">
      <h1 className="text-lg font-semibold text-gray-900">{booking.serviceName ?? t('manage.fallbackTitle')}</h1>
      <p className="mt-1 text-sm text-gray-600" data-testid="manage-when">{whenLabel(booking.scheduledAt)}</p>
      <p className="mt-0.5 text-xs text-gray-400">{t('manage.duration', { minutes: booking.durationMin })}</p>

      {movedTo !== null && (
        <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700" data-testid="manage-moved">
          {t('manage.moved')}
        </p>
      )}

      {booking.joinUrl !== null && (
        <a
          href={booking.joinUrl}
          className="mt-4 inline-block rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white"
        >
          {t('manage.join')}
        </a>
      )}

      {actionError !== null && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="manage-error">
          {actionError}
        </p>
      )}

      <div className="mt-8 flex gap-2 border-t border-gray-100 pt-6" data-cookie-avoid>
        {/* Rescheduling needs a live event type to draw slots from; a booking
            whose service was archived can still be cancelled. */}
        {booking.bookingSlug !== null && (
          <button
            type="button"
            onClick={() => void openPicker()}
            disabled={busy}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            data-testid="manage-reschedule"
          >
            {t('manage.pickNewTime')}
          </button>
        )}
        <button
          type="button"
          onClick={() => setConfirmingCancel(true)}
          disabled={busy}
          className="rounded-lg px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          data-testid="manage-cancel"
        >
          {t('manage.cancel')}
        </button>
      </div>

      <ManageCancelConfirm
        open={confirmingCancel}
        busy={busy}
        onConfirm={() => void cancel()}
        onKeep={() => setConfirmingCancel(false)}
      />

      {picking && (
        <div className="mt-6 border-t border-gray-100 pt-6" data-testid="manage-slots">
          {grouped.length === 0 ? (
            <p className="text-sm text-gray-400">{t('manage.noOtherTimes')}</p>
          ) : (
            grouped.map(([day, daySlots]) => (
              <div key={day} className="mb-4">
                <p className="mb-2 text-xs font-medium text-gray-500">{dayLabel(daySlots[0].start)}</p>
                <div className="flex flex-wrap gap-2">
                  {daySlots.map((s) => (
                    <button
                      key={s.start}
                      type="button"
                      disabled={busy}
                      onClick={() => void reschedule(s.start)}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      {timeLabel(s.start)}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
